"use client";

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "cn";
import * as React from "react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { CloseIcon } from "../icons/CloseIcon";
import { MenuIcon } from "../icons/MenuIcon";
import { PanelLeftIcon } from "../icons/PanelLeftIcon";
import { SearchIcon } from "../icons/SearchIcon";
import { AppSidebarPattern } from "./AppSidebarPattern";
import { Avatar, AvatarFallback, AvatarImage } from "./Avatar";
import { CanteraLogo } from "./CanteraLogo";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./Tooltip";

/** How long the hover-expanded rail stays open after the pointer leaves it. */
const RAIL_CLOSE_DELAY = 150;

/** Matches Tailwind's `md`. The off-canvas sheet only exists below it. */
const WIDE_VIEWPORT_QUERY = "(min-width: 48rem)";
const SIDEBAR_WIDTHS = "[--app-sidebar-width:16.75rem] [--app-sidebar-rail-width:calc(var(--spacing)*12)]";

/** `shadow-lg` cast to the right, for the hover-expanded rail docked on the left. */
const SIDE_SHADOW = "shadow-[4px_0px_6px_-4px_rgba(0,0,0,0.1),10px_0px_15px_-3px_rgba(0,0,0,0.1)]";
const panelSurface = cn("flex flex-col overflow-clip", "bg-sidebar text-sidebar-foreground", "border-r border-sidebar-border");
type AppSidebarContextValue = {
  pinned: boolean;
  hoverOpen: boolean;
  onSearch?: () => void;
  setSheetOpen: (open: boolean) => void;
  togglePinned: () => void;
  pointerEnter: () => void;
  pointerLeave: () => void;
  focusEnter: () => void;
  focusLeave: () => void;
};
const AppSidebarContext = React.createContext<AppSidebarContextValue | null>(null);
function useAppSidebar() {
  const context = React.useContext(AppSidebarContext);
  if (!context) {
    throw new Error("AppSidebar parts must be rendered inside AppSidebarLayout.");
  }
  return context;
}
type AppSidebarPanelContextValue = {
  /** `rail` is the desktop panel; `sheet` is the off-canvas panel below md. */
  variant: "rail" | "sheet";
  expanded: boolean;
};
const AppSidebarPanelContext = React.createContext<AppSidebarPanelContextValue>({
  variant: "rail",
  expanded: true
});
type AppSidebarLayoutProps = React.ComponentProps<"div"> & {
  /** The `AppSidebar` element. */
  sidebar: React.ReactNode;
  /** Whether the sidebar is pinned open, pushing the content aside. */
  pinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
  /** Called by the header search button. The button is hidden when omitted. */
  onSearch?: () => void;
};

/**
 * Page shell containing the app sidebar and main content area. Owns the
 * sidebar's shared state: the controlled pinned flag, hover-expanded rail,
 * and off-canvas sheet.
 */
function AppSidebarLayout(t0) {
  const $ = _c(42);
  let children;
  let className;
  let onPinnedChange;
  let onSearch;
  let pinned;
  let props;
  let sidebar;
  if ($[0] !== t0) {
    ({
      sidebar,
      pinned,
      onPinnedChange,
      onSearch,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onPinnedChange;
    $[4] = onSearch;
    $[5] = pinned;
    $[6] = props;
    $[7] = sidebar;
  } else {
    children = $[1];
    className = $[2];
    onPinnedChange = $[3];
    onSearch = $[4];
    pinned = $[5];
    props = $[6];
    sidebar = $[7];
  }
  const [hoverOpen, setHoverOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const closeTimer = React.useRef(undefined);
  const pointerInside = React.useRef(false);
  const focusInside = React.useRef(false);
  let t1;
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => window.clearTimeout(closeTimer.current);
    t2 = [];
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  React.useEffect(t1, t2);
  const wideViewport = useMediaQuery(WIDE_VIEWPORT_QUERY);
  let t3;
  let t4;
  if ($[10] !== wideViewport) {
    t3 = () => {
      if (wideViewport) {
        setSheetOpen(false);
      }
    };
    t4 = [wideViewport];
    $[10] = wideViewport;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      window.clearTimeout(closeTimer.current);
      setHoverOpen(true);
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const open = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => {
        if (!pointerInside.current && !focusInside.current) {
          setHoverOpen(false);
        }
      }, RAIL_CLOSE_DELAY);
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const scheduleClose = t6;
  let t7;
  if ($[15] !== onPinnedChange || $[16] !== pinned) {
    t7 = () => {
      window.clearTimeout(closeTimer.current);
      setHoverOpen(false);
      onPinnedChange(!pinned);
    };
    $[15] = onPinnedChange;
    $[16] = pinned;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      pointerInside.current = true;
      open();
    };
    t9 = () => {
      pointerInside.current = false;
      scheduleClose();
    };
    t10 = () => {
      focusInside.current = true;
      open();
    };
    t11 = () => {
      focusInside.current = false;
      scheduleClose();
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[22] !== hoverOpen || $[23] !== onSearch || $[24] !== pinned || $[25] !== t7) {
    t12 = {
      pinned,
      hoverOpen,
      onSearch,
      setSheetOpen,
      togglePinned: t7,
      pointerEnter: t8,
      pointerLeave: t9,
      focusEnter: t10,
      focusLeave: t11
    };
    $[22] = hoverOpen;
    $[23] = onSearch;
    $[24] = pinned;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const context = t12;
  let t13;
  if ($[27] !== className) {
    t13 = cn("flex min-h-svh w-full", SIDEBAR_WIDTHS, className);
    $[27] = className;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== children) {
    t14 = <div data-slot="app-sidebar-inset" className="flex min-w-0 flex-1 flex-col">{children}</div>;
    $[29] = children;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== props || $[32] !== sidebar || $[33] !== t13 || $[34] !== t14) {
    t15 = <div data-slot="app-sidebar-layout" className={t13} {...props}>{sidebar}{t14}</div>;
    $[31] = props;
    $[32] = sidebar;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== sheetOpen || $[37] !== t15) {
    t16 = <TooltipProvider><DialogPrimitive.Root open={sheetOpen} onOpenChange={setSheetOpen}>{t15}</DialogPrimitive.Root></TooltipProvider>;
    $[36] = sheetOpen;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== context || $[40] !== t16) {
    t17 = <AppSidebarContext.Provider value={context}>{t16}</AppSidebarContext.Provider>;
    $[39] = context;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}
type AppSidebarProps = React.ComponentProps<"div"> & {
  /** Accessible name of the sidebar's navigation landmark and of the off-canvas sheet. */
  label?: string;
};

/**
 * The sidebar panel. From md up it is a fixed icon rail that expands on hover,
 * or a full-width panel when pinned. Below md it is hidden and its parts render
 * in an off-canvas sheet opened by `AppSidebarTrigger`.
 */
function AppSidebar(t0) {
  const $ = _c(65);
  let children;
  let className;
  let onPointerEnter;
  let onPointerLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label: t1,
      className,
      children,
      onPointerEnter,
      onPointerLeave,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onPointerEnter;
    $[4] = onPointerLeave;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    onPointerEnter = $[3];
    onPointerLeave = $[4];
    props = $[5];
    t1 = $[6];
  }
  const label = t1 === undefined ? "Main navigation" : t1;
  const {
    pinned,
    hoverOpen,
    pointerEnter,
    pointerLeave,
    focusEnter,
    focusLeave
  } = useAppSidebar();
  const expanded = pinned || hoverOpen;
  const pointerDown = React.useRef(false);
  let t2;
  if ($[7] !== expanded) {
    t2 = {
      variant: "rail",
      expanded
    };
    $[7] = expanded;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const railContext = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      variant: "sheet",
      expanded: true
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const sheetContext = t3;
  const t4 = pinned ? "w-(--app-sidebar-width)" : "w-(--app-sidebar-rail-width)";
  let t5;
  if ($[10] !== t4) {
    t5 = cn("hidden shrink-0 md:block", "transition-[width] duration-200 ease-linear motion-reduce:transition-none", t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <div aria-hidden="true" data-slot="app-sidebar-gap" className={t5} />;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = expanded ? "expanded" : "collapsed";
  const t8 = expanded ? "w-(--app-sidebar-width)" : "w-(--app-sidebar-rail-width)";
  let t9;
  if ($[14] !== expanded || $[15] !== pinned) {
    t9 = expanded && !pinned && ["rounded-r-lg", SIDE_SHADOW];
    $[14] = expanded;
    $[15] = pinned;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== className || $[18] !== t8 || $[19] !== t9) {
    t10 = cn(panelSurface, "group/app-sidebar fixed inset-y-0 left-0 z-30 hidden md:flex", "transition-[width,box-shadow,border-radius] duration-200 ease-linear motion-reduce:transition-none", t8, t9, className);
    $[17] = className;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== onPointerEnter || $[22] !== pointerEnter) {
    t11 = event => {
      onPointerEnter?.(event);
      if (event.pointerType !== "touch") {
        pointerEnter();
      }
    };
    $[21] = onPointerEnter;
    $[22] = pointerEnter;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== onPointerLeave || $[25] !== pointerLeave) {
    t12 = event_0 => {
      onPointerLeave?.(event_0);
      if (event_0.pointerType !== "touch") {
        pointerLeave();
      }
    };
    $[24] = onPointerLeave;
    $[25] = pointerLeave;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AppSidebarPattern className="absolute top-0 left-0 text-marble-600" />;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      pointerDown.current = true;
    };
    t15 = () => {
      pointerDown.current = false;
    };
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  let t16;
  if ($[30] !== focusEnter) {
    t16 = () => {
      if (!pointerDown.current) {
        focusEnter();
      }
      pointerDown.current = false;
    };
    $[30] = focusEnter;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== focusLeave) {
    t17 = event_1 => {
      if (!event_1.currentTarget.contains(event_1.relatedTarget)) {
        focusLeave();
      }
    };
    $[32] = focusLeave;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== children || $[35] !== label || $[36] !== t16 || $[37] !== t17) {
    t18 = <nav aria-label={label} className="relative flex min-h-0 w-full flex-1 flex-col" onPointerDown={t14} onPointerUp={t15} onFocus={t16} onBlur={t17}>{children}</nav>;
    $[34] = children;
    $[35] = label;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== railContext || $[40] !== t18) {
    t19 = <AppSidebarPanelContext.Provider value={railContext}>{t13}{t18}</AppSidebarPanelContext.Provider>;
    $[39] = railContext;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== pinned || $[43] !== props || $[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t19 || $[48] !== t7) {
    t20 = <div data-slot="app-sidebar" data-state={t7} data-pinned={pinned} className={t10} onPointerEnter={t11} onPointerLeave={t12} {...props}>{t19}</div>;
    $[42] = pinned;
    $[43] = props;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t19;
    $[48] = t7;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DialogPrimitive.Overlay data-slot="app-sidebar-overlay" className={cn("fixed inset-0 z-50 bg-black/50 md:hidden", "data-[state=open]:animate-in data-[state=open]:fade-in-0", "data-[state=closed]:animate-out data-[state=closed]:fade-out-0")} />;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = cn(panelSurface, SIDEBAR_WIDTHS, "fixed inset-y-0 left-0 z-50 md:hidden outline-none", "w-(--app-sidebar-width) max-w-[calc(100%-4rem)]", "data-[state=open]:animate-in data-[state=open]:slide-in-from-left", "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left", "duration-200");
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== label) {
    t23 = <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>;
    $[52] = label;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <AppSidebarPattern className="absolute top-0 left-0 text-marble-600" />;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== children || $[56] !== label) {
    t25 = <AppSidebarPanelContext.Provider value={sheetContext}>{t24}<nav aria-label={label} data-state="expanded" className="group/app-sidebar relative flex min-h-0 w-full flex-1 flex-col">{children}</nav></AppSidebarPanelContext.Provider>;
    $[55] = children;
    $[56] = label;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t23 || $[59] !== t25) {
    t26 = <DialogPrimitive.Portal>{t21}<DialogPrimitive.Content data-slot="app-sidebar-sheet" aria-describedby={undefined} className={t22}>{t23}{t25}</DialogPrimitive.Content></DialogPrimitive.Portal>;
    $[58] = t23;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t20 || $[62] !== t26 || $[63] !== t6) {
    t27 = <>{t6}{t20}{t26}</>;
    $[61] = t20;
    $[62] = t26;
    $[63] = t6;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  return t27;
}
const iconButtonClasses = cn("flex size-8 shrink-0 items-center justify-center rounded-md cursor-pointer", "text-marble-600 outline-none transition-colors", "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "aria-pressed:text-sidebar-foreground", "focus-visible:shadow-[0_0_0_3px_var(--sidebar-ring)]");
type HeaderButtonProps = React.ComponentProps<"button"> & {
  label: string;
};

/**
 * Icon-only header control. Tooltips are skipped in the sheet because the
 * dialog's initial focus would otherwise open one immediately.
 */
function HeaderButton(t0) {
  const $ = _c(17);
  let className;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = label;
    $[3] = props;
  } else {
    className = $[1];
    label = $[2];
    props = $[3];
  }
  const {
    variant
  } = React.useContext(AppSidebarPanelContext);
  let t1;
  if ($[4] !== className) {
    t1 = cn(iconButtonClasses, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== label || $[7] !== props || $[8] !== t1) {
    t2 = <button type="button" aria-label={label} className={t1} {...props} />;
    $[6] = label;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const button = t2;
  if (variant === "sheet") {
    return button;
  }
  let t3;
  if ($[10] !== button) {
    t3 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[10] = button;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== label) {
    t4 = <TooltipContent side="bottom">{label}</TooltipContent>;
    $[12] = label;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
type AppSidebarHeaderProps = Omit<React.ComponentProps<"div">, "children">;
function AppSidebarHeader(t0) {
  const $ = _c(20);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    pinned,
    onSearch,
    togglePinned,
    setSheetOpen
  } = useAppSidebar();
  const {
    variant
  } = React.useContext(AppSidebarPanelContext);
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex shrink-0 items-center justify-between gap-2", "px-2 pt-6 pb-3", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-8 items-center px-2"><CanteraLogo variant="logo" size="sm" className="group-data-[state=collapsed]/app-sidebar:hidden" /><CanteraLogo variant="symbol" size="xs" className="hidden group-data-[state=collapsed]/app-sidebar:block" /></div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onSearch) {
    t3 = onSearch && <HeaderButton label="Search" onClick={onSearch}><SearchIcon /></HeaderButton>;
    $[6] = onSearch;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== pinned || $[9] !== setSheetOpen || $[10] !== togglePinned || $[11] !== variant) {
    t4 = variant === "sheet" ? <HeaderButton label="Close navigation" onClick={() => setSheetOpen(false)}><CloseIcon /></HeaderButton> : <HeaderButton label="Pin sidebar" aria-pressed={pinned} onClick={togglePinned}><PanelLeftIcon /></HeaderButton>;
    $[8] = pinned;
    $[9] = setSheetOpen;
    $[10] = togglePinned;
    $[11] = variant;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex items-center gap-0.5 group-data-[state=collapsed]/app-sidebar:hidden">{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== props || $[17] !== t1 || $[18] !== t5) {
    t6 = <div data-slot="app-sidebar-header" className={t1} {...props}>{t2}{t5}</div>;
    $[16] = props;
    $[17] = t1;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

/** Scrollable area holding the sidebar groups. */
function AppSidebarContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-4", "overflow-x-hidden overflow-y-auto px-2 py-3", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="app-sidebar-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type AppSidebarGroupProps = React.ComponentProps<"div"> & {
  /** Group heading. It keeps its space but is invisible on the collapsed rail. */
  label?: string;
};
function AppSidebarGroup(t0) {
  const $ = _c(16);
  let children;
  let className;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = label;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    label = $[3];
    props = $[4];
  }
  const labelId = React.useId();
  const t1 = label ? labelId : undefined;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex flex-col gap-0.5", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== label || $[8] !== labelId) {
    t3 = label && <div id={labelId} data-slot="app-sidebar-group-label" className={cn("flex h-8 items-center px-2", "heading-xs text-marble-600 truncate whitespace-nowrap", "transition-opacity group-data-[state=collapsed]/app-sidebar:opacity-0")}>{label}</div>;
    $[7] = label;
    $[8] = labelId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== props || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <div role="group" aria-labelledby={t1} data-slot="app-sidebar-group" className={t2} {...props}>{t3}{children}</div>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
type AppSidebarItemProps = Omit<React.ComponentProps<"a">, "children"> & {
  icon: React.ReactNode;
  label: string;
  /** Marks the item as the current page. The app decides; the sidebar never reads the URL. */
  active?: boolean;
  /** Render the single child element (e.g. a framework `Link`) as the item. */
  asChild?: boolean;
  children?: React.ReactElement;
};

/**
 * Navigation entry. Renders an `<a>` when given `href`, a `<button>` otherwise,
 * or the child element with `asChild`.
 */
function AppSidebarItem(t0) {
  const $ = _c(33);
  let children;
  let className;
  let icon;
  let label;
  let onClick;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      icon,
      label,
      active: t1,
      asChild: t2,
      className,
      children,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = label;
    $[5] = onClick;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    label = $[4];
    onClick = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const active = t1 === undefined ? false : t1;
  const asChild = t2 === undefined ? false : t2;
  const {
    setSheetOpen
  } = useAppSidebar();
  const {
    variant
  } = React.useContext(AppSidebarPanelContext);
  const Comp = asChild ? Slot : props.href ? "a" : "button";
  const t3 = active || undefined;
  const t4 = active ? "page" : undefined;
  const t5 = Comp === "button" ? "button" : undefined;
  let t6;
  if ($[9] !== className) {
    t6 = cn("flex h-9 w-full shrink-0 items-center gap-3 overflow-hidden rounded-md", "px-3 group-data-[state=collapsed]/app-sidebar:p-2 group-data-[state=collapsed]/app-sidebar:h-8 group-data-[state=collapsed]/app-sidebar:w-8 cursor-pointer", "body-md whitespace-nowrap text-marble-600 outline-none transition-[color,background-color,width,height]", "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "aria-[current=page]:bg-deepjade-800 aria-[current=page]:text-sidebar-accent-foreground", "focus-visible:shadow-[0_0_0_3px_var(--sidebar-ring)]", className);
    $[9] = className;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onClick || $[12] !== setSheetOpen || $[13] !== variant) {
    t7 = event => {
      onClick?.(event);
      if (variant === "sheet" && !event.defaultPrevented) {
        setSheetOpen(false);
      }
    };
    $[11] = onClick;
    $[12] = setSheetOpen;
    $[13] = variant;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== asChild || $[16] !== children) {
    t8 = asChild && <Slottable>{children}</Slottable>;
    $[15] = asChild;
    $[16] = children;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== icon) {
    t9 = <span aria-hidden="true" className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>;
    $[18] = icon;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== label) {
    t10 = <span className="truncate transition-opacity group-data-[state=collapsed]/app-sidebar:opacity-0">{label}</span>;
    $[20] = label;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== Comp || $[23] !== props || $[24] !== t10 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t11 = <div className="flex h-9 items-center"><Comp data-slot="app-sidebar-item" data-active={t3} aria-current={t4} type={t5} className={t6} onClick={t7} {...props}>{t8}{t9}{t10}</Comp></div>;
    $[22] = Comp;
    $[23] = props;
    $[24] = t10;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function AppSidebarFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-[color-mix(in_oklab,var(--color-deepjade-950)_12%,var(--sidebar))]", "flex shrink-0 items-center border-t border-sidebar-border", "p-4 group-data-[state=collapsed]/app-sidebar:px-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="app-sidebar-footer" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type AppSidebarUserProps = Omit<React.ComponentProps<"div">, "children"> & {
  name: string;
  email?: string;
  /** Avatar image URL. Initials are shown while it loads or when absent. */
  image?: string;
  /** Avatar fallback. Defaults to the first letters of the first two words of `name`. */
  initials?: string;
};
function getInitials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]?.toUpperCase()).join("");
}

/** Display-only identity block for the sidebar footer. */
function AppSidebarUser(t0) {
  const $ = _c(31);
  let className;
  let email;
  let image;
  let initials;
  let name;
  let props;
  if ($[0] !== t0) {
    ({
      name,
      email,
      image,
      initials,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = email;
    $[3] = image;
    $[4] = initials;
    $[5] = name;
    $[6] = props;
  } else {
    className = $[1];
    email = $[2];
    image = $[3];
    initials = $[4];
    name = $[5];
    props = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn("flex min-w-0 flex-1 items-center gap-3", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== image) {
    t2 = image && <AvatarImage src={image} alt="" />;
    $[9] = image;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== initials || $[12] !== name) {
    t3 = initials ?? getInitials(name);
    $[11] = initials;
    $[12] = name;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <AvatarFallback>{t3}</AvatarFallback>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t4) {
    t5 = <Avatar size="sm" aria-hidden="true">{t2}{t4}</Avatar>;
    $[16] = t2;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== name) {
    t6 = <span className="truncate label-md text-sidebar-foreground">{name}</span>;
    $[19] = name;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== email) {
    t7 = email && <span className="truncate caption-sm text-marble-600">{email}</span>;
    $[21] = email;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <div className="grid min-w-0 flex-1 whitespace-nowrap transition-opacity group-data-[state=collapsed]/app-sidebar:opacity-0">{t6}{t7}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== props || $[27] !== t1 || $[28] !== t5 || $[29] !== t8) {
    t9 = <div data-slot="app-sidebar-user" className={t1} {...props}>{t5}{t8}</div>;
    $[26] = props;
    $[27] = t1;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}

/**
 * Opens the off-canvas sheet. Hidden at `md` and above (>=48rem).
 */
function AppSidebarTrigger(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("inline-flex size-9 shrink-0 items-center justify-center rounded-sm cursor-pointer md:hidden", "text-foreground outline-none transition-colors", "hover:bg-accent hover:text-accent-foreground", "focus-visible:ring-[3px] focus-visible:ring-ring/50", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = children ?? <MenuIcon />;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t3 = <DialogPrimitive.Trigger data-slot="app-sidebar-trigger" aria-label="Open navigation" className={t1} {...props}>{t2}</DialogPrimitive.Trigger>;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export { AppSidebar, AppSidebarContent, AppSidebarFooter, AppSidebarGroup, AppSidebarHeader, AppSidebarItem, AppSidebarLayout, AppSidebarTrigger, AppSidebarUser, type AppSidebarGroupProps, type AppSidebarHeaderProps, type AppSidebarItemProps, type AppSidebarLayoutProps, type AppSidebarProps, type AppSidebarUserProps };